import { BadgeCheck, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ModelPricingRecord } from '@nadoutong/contracts';
import { useChannelModelsList } from '../api/useChannelModels.ts';
import { useChannelsList } from '../api/useChannels.ts';
import { formatRateMultiplier } from './formatters.ts';
import { useT } from '../i18n/index.ts';
import { Panel, Skeleton, Table, TBody, Td, Th, THead, Tr } from '../ui/index.ts';

interface Props {
  readonly vendor: string;
  readonly model: string;
  readonly currentPricing: ModelPricingRecord | null;
}

/**
 * "Which namespaces carry this model" card (epic #898 P6, #910): one row
 * per SKU, priced at the SKU grain (`channel_models.rate_multiplier`,
 * #900), each row linking to its SKU detail page in the Model Market.
 */
export function ChannelPricingCard({ vendor, model, currentPricing }: Props): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const { data: allModels, isLoading: loadingModels } = useChannelModelsList();
  const { data: channels, isLoading: loadingChannels } = useChannelsList();

  if (loadingModels || loadingChannels) {
    return (
      <Panel caption={t('model_detail.channel_pricing.title')} padded>
        <div className="flex flex-col gap-2">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      </Panel>
    );
  }

  const entries = (allModels ?? []).filter(
    (cm) => cm.model === model,
  );
  const channelMap = new Map((channels ?? []).map((ch) => [ch.id, ch]));

  if (entries.length === 0) {
    return (
      <Panel caption={t('model_detail.channel_pricing.title')} padded>
        <p className="m-0 text-body text-paper-low">{t('model_detail.channel_pricing.empty')}</p>
      </Panel>
    );
  }

  return (
    <Panel
      caption={t('model_detail.channel_pricing.title')}
      count={entries.length}
      testId="channel-pricing-card"
    >
      <Table className="rounded-none border-0">
        <THead>
          <Tr interactive={false}>
            <Th>{t('model_detail.channel_pricing.col_channel')}</Th>
            <Th>{t('model_detail.channel_pricing.col_multiplier')}</Th>
            <Th num>{t('model_detail.channel_pricing.col_input_price')}</Th>
            <Th num>{t('model_detail.channel_pricing.col_output_price')}</Th>
            <Th>{t('model_detail.channel_pricing.col_supplier')}</Th>
            <Th />
          </Tr>
        </THead>
        <TBody>
          {entries.map((cm) => {
            const ch = channelMap.get(cm.channel_id);
            const rate = cm.rate_multiplier;
            const inputPrice = currentPricing !== null
              ? (currentPricing.input_per_mtok_usd * rate).toFixed(2)
              : '—';
            const outputPrice = currentPricing !== null
              ? (currentPricing.output_per_mtok_usd * rate).toFixed(2)
              : '—';
            return (
              <Tr
                key={cm.sku_id}
                className="group cursor-pointer"
                data-testid={`channel-pricing-row-${cm.sku_id}`}
                onClick={() => navigate(`/market/${encodeURIComponent(cm.sku_id)}`)}
              >
                <Td>
                  <div className="flex items-center gap-2">
                    <span className="text-body font-medium text-paper">{ch?.display_name ?? cm.channel_id}</span>
                    {ch?.is_official ? (
                      <BadgeCheck aria-hidden="true" className="h-3.5 w-3.5 text-signal" />
                    ) : null}
                  </div>
                  <span className="font-mono text-label text-paper-low">{cm.sku_name}</span>
                </Td>
                <Td mono emphasis>×{formatRateMultiplier(rate)}</Td>
                <Td num emphasis>${inputPrice}</Td>
                <Td num emphasis>${outputPrice}</Td>
                <Td>{ch?.supplier_name ?? '—'}</Td>
                <Td className="text-right">
                  <ChevronRight
                    aria-hidden="true"
                    className="h-4 w-4 text-paper-low transition-colors duration-120 group-hover:text-signal"
                  />
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </Panel>
  );
}
